import { type JSX, useState } from 'react';
import Typography from 'carbon-react/esm/components/typography';
import Box from 'carbon-react/esm/components/box';
import Button from 'carbon-react/esm/components/button/__next__';
import CollaboratorTable from './CollaboratorsList';
import AddCollaboratorDialog from './AddCollaboratorDialog';
import { useCollaborators } from '../../../../hooks/useCollaborators';
import { useProfilesContext } from '../../../../context/useProfilesContext';
import { Collaborator, EmptyCollaborator } from '../../../../types/collaborator.types';
import { useNotificationWithAutoClose } from '../../../../utils/notificationHelpers';
import { NotificationMessage } from '../../../../components/NotificationMessage';
import AdminLoaderItems from '../AdminLoaderItems';
import AdminErrorItems from '../AdminErrorItems';
import { useResponsive } from '../../../../hooks/useResponsive';
import DeleteCollaboratorDialog from './DeleteCollaboratorDialog';
import EditCollaboratorDialog from './EditCollaboratorDialog';

const CollaboratorTab = (): JSX.Element => {
  // Dialog states
  const [editDialogId, setEditDialogId] = useState<string | null>(null);
  const [deleteDialogId, setDeleteDialogId] = useState<string | null>(null);
  const [selectedCollaborator, setSelectedCollaborator] = useState<Collaborator>(EmptyCollaborator);
  const [isAddUserDialogOpen, setIsAddUserDialogOpen] = useState(false);

  const { isMobile } = useResponsive();
  const { collaborators, isLoading, error, addCollaborator, editCollaborator, deleteCollaborator } =
    useCollaborators();
  const { profiles } = useProfilesContext();
  const {
    isVisibleNotification,
    notificationMessage,
    notificationVariant,
    displayNotification,
    hideNotification,
  } = useNotificationWithAutoClose();
  const handleAddUser = async (
    newCollaborator: Collaborator,
    confirmEmail: string,
  ): Promise<{ success: boolean; error?: string }> => {
    return await addCollaborator(newCollaborator, confirmEmail);
  };

  if (isLoading) {
    return <AdminLoaderItems message="Chargement des collaborateurs..." />;
  }

  if (error) {
    return <AdminErrorItems error={error} />;
  }

  return (
    <Box bg="white" p="23px" width="100%" minWidth={0} boxSizing="border-box">
      <Typography variant="h2" mb={16}>
        Liste des collaborateurs
      </Typography>
      <Box display="flex" justifyContent={isMobile ? 'flex-center' : 'flex-end'} mb={2}>
        <Button
          onClick={() => setIsAddUserDialogOpen(true)}
          variantType="primary"
          size={isMobile ? 'small' : 'medium'}
          iconType="add"
          fullWidth={isMobile}
        >
          Ajouter un collaborateur
        </Button>
      </Box>

      <NotificationMessage
        open={isVisibleNotification}
        onDismiss={hideNotification}
        variant={notificationVariant}
      >
        {notificationMessage}
      </NotificationMessage>

      <CollaboratorTable
        collaborators={collaborators}
        profiles={profiles}
        setSelectedCollaborator={setSelectedCollaborator}
        setEditDialogId={setEditDialogId}
        setDeleteDialogId={setDeleteDialogId}
      />

      <AddCollaboratorDialog
        isOpen={isAddUserDialogOpen}
        onClose={() => setIsAddUserDialogOpen(false)}
        onSave={handleAddUser}
        onSuccess={message => displayNotification(message, 'success')}
        onError={error => {
          const errorMessage = error instanceof Error ? error.message : 'Une erreur est survenue';
          displayNotification(errorMessage, 'error');
        }}
      />

      <EditCollaboratorDialog
        isOpen={editDialogId !== null}
        onClose={() => setEditDialogId(null)}
        onSave={updatedCollaborator => editCollaborator(updatedCollaborator)}
        collaborator={selectedCollaborator}
        onSuccess={message => displayNotification(message, 'success')}
        onError={error => {
          const errorMessage =
            error instanceof Error
              ? error.message
              : 'Une erreur est survenue lors de la modification du collaborateur';
          displayNotification(errorMessage, 'error');
        }}
      />

      <DeleteCollaboratorDialog
        isOpen={deleteDialogId !== null}
        collaborator={selectedCollaborator}
        onClose={() => setDeleteDialogId(null)}
        onConfirm={() =>
          deleteDialogId
            ? deleteCollaborator(deleteDialogId)
            : Promise.resolve({ success: false, error: 'No collaborator selected' })
        }
        onSuccess={message => displayNotification(message, 'success')}
        onError={error => {
          const errorMessage =
            error instanceof Error
              ? error.message
              : 'Une erreur est survenue lors de la suppression du collaborateur';
          displayNotification(errorMessage, 'error');
        }}
      />
    </Box>
  );
};

export default CollaboratorTab;
